import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { useError } from '../contexts/ErrorContext';
import {
  fetchReleaseProducts, fetchReleases, createRelease, updateRelease, deleteRelease,
} from '../services/releasesService';
import './ReleaseManagementPage.css';

// Fallback until /api/releases/products answers; same order as the backend.
const DEFAULT_PRODUCTS = [
  { key: 'android_app', label: 'Android App REKKIE' },
  { key: 'ios_app', label: 'iOS App REKKIE' },
  { key: 'offboard', label: 'Offboard Product' },
  { key: 'map_database', label: 'Map Database' },
  { key: 'firmware', label: 'REKKIE Firmware' },
];

// Fallback until /api/releases/products answers; same order as the backend.
const DEFAULT_FIRMWARE_VARIANTS = [
  { key: 'classic_22', label: 'Classic 22 (2023 MY)' },
  { key: 'multifun_23', label: 'Multifun 23 (2024 MY)' },
  { key: 'display_24', label: 'Display 24 (2025 MY)' },
  { key: 'magnetic_25', label: 'Magnetic 25 (2026 MY)' },
  { key: 'display_v2_26', label: 'Display v2 26 (2027 MY)' },
];

// Channel is free text; these are the usual values per product, offered as
// suggestions so spelling stays consistent across entries.
const CHANNEL_SUGGESTIONS = {
  android_app: ['Internal Testing', 'Closed Beta', 'Open Beta', 'Production'],
  ios_app: ['TestFlight Internal', 'TestFlight External', 'App Store'],
  offboard: ['Production'],
  map_database: ['Live'],
  firmware: ['public', 'beta', 'internal', 'alpha'],
};

const VERSION_PLACEHOLDER = {
  android_app: '2.1.2',
  ios_app: '2.1.2',
  offboard: '2026.10.11',
  map_database: 'v12',
  firmware: '1.2.3.45',
};

const PRODUCT_BADGE_COLOR = {
  android_app: '#3ddc84',
  ios_app: '#0ea5e9',
  offboard: '#8b5cf6',
  map_database: '#f59e0b',
  firmware: '#ef4444',
};

// The form's datetime-local input works in the viewer's zone as a wall-clock
// string ("2026-10-11T12:34:56"); the API stores an absolute ISO instant.
const pad = (n) => String(n).padStart(2, '0');
const instantToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const localInputToInstant = (wall) => {
  if (!wall) return null;
  const d = new Date(wall); // no zone suffix → parsed as local time
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
const now = () => instantToLocalInput(new Date().toISOString());

const formatInstant = (iso) => {
  if (!iso) return 'no date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString([], {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const emptyForm = () => ({
  version: '',
  channel: '',
  variants: [],
  releasedAt: now(),
  title: '',
  changesText: '',
  notes: '',
  link: '',
});

const formFromRelease = (r) => ({
  version: r.version || '',
  channel: r.channel || '',
  variants: r.variants || [],
  releasedAt: instantToLocalInput(r.releasedAt),
  title: r.title || '',
  changesText: (r.changes || []).join('\n'),
  notes: r.notes || '',
  link: r.link || '',
});

const payloadFromForm = (product, f) => ({
  product,
  version: f.version.trim(),
  channel: f.channel.trim(),
  variants: product === 'firmware' ? f.variants : [],
  releasedAt: localInputToInstant(f.releasedAt),
  title: f.title.trim(),
  changes: f.changesText.split('\n').map((s) => s.replace(/^\s*[-*•]\s*/, '').trim()).filter(Boolean),
  notes: f.notes.trim(),
  link: f.link.trim(),
});

const ReleaseManagementPage = () => {
  const { addError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const [firmwareVariants, setFirmwareVariants] = useState(DEFAULT_FIRMWARE_VARIANTS);
  const variantKeys = useMemo(() => firmwareVariants.map((v) => v.key), [firmwareVariants]);
  const variantLabel = useCallback(
    (key) => firmwareVariants.find((v) => v.key === key)?.label || key,
    [firmwareVariants],
  );
  const productKeys = useMemo(() => products.map((p) => p.key), [products]);

  const urlProduct = searchParams.get('product');
  const product = productKeys.includes(urlProduct) ? urlProduct : productKeys[0];
  const productLabel = products.find((p) => p.key === product)?.label || product;

  const [releases, setReleases] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [variantFilter, setVariantFilter] = useState('');

  const [editing, setEditing] = useState(null); // null | 'new' | releaseId
  const [form, setForm] = useState(emptyForm(product));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchReleaseProducts()
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data?.products) && data.products.length) setProducts(data.products);
        if (Array.isArray(data?.firmwareVariants) && data.firmwareVariants.length) setFirmwareVariants(data.firmwareVariants);
      })
      .catch(() => { /* fall back to DEFAULT_PRODUCTS */ });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReleases(await fetchReleases(product));
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Release Management');
    }
  }, [product, addError]);

  useEffect(() => {
    setReleases(null);
    setChannelFilter('');
    setVariantFilter('');
    load();
  }, [load]);

  const selectProduct = (key) => {
    setSearchParams(key === productKeys[0] ? {} : { product: key }, { replace: true });
  };

  const channels = useMemo(() => {
    const seen = new Set((releases || []).map((r) => r.channel).filter(Boolean));
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [releases]);

  const shown = useMemo(() => {
    if (!releases) return [];
    const q = filter.trim().toLowerCase();
    return releases.filter((r) => {
      if (channelFilter && r.channel !== channelFilter) return false;
      if (variantFilter && !(r.variants || []).includes(variantFilter)) return false;
      if (!q) return true;
      return (r.version || '').toLowerCase().includes(q)
        || (r.channel || '').toLowerCase().includes(q)
        || (r.title || '').toLowerCase().includes(q)
        || (r.notes || '').toLowerCase().includes(q)
        || (r.changes || []).some((c) => c.toLowerCase().includes(q))
        || (r.createdByEmail || '').toLowerCase().includes(q);
    });
  }, [releases, filter, channelFilter, variantFilter]);

  // The newest release per channel is what is "on" that channel right now.
  const currentByChannel = useMemo(() => {
    const out = {};
    (releases || []).forEach((r) => {
      if (r.channel && !out[r.channel]) out[r.channel] = r.releaseId;
    });
    return out;
  }, [releases]);

  const openNew = () => {
    setForm(emptyForm());
    setFormError(null);
    setEditing('new');
  };

  const openEdit = (r) => {
    setForm(formFromRelease(r));
    setFormError(null);
    setEditing(r.releaseId);
  };

  const closeForm = () => { if (!saving) setEditing(null); };

  const setField = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const toggleVariant = (variant) => setForm((f) => ({
    ...f,
    variants: f.variants.includes(variant)
      ? f.variants.filter((v) => v !== variant)
      : variantKeys.filter((v) => v === variant || f.variants.includes(v)),
  }));

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    const payload = payloadFromForm(product, form);
    if (!payload.version) return setFormError('Version is required.');
    if (!payload.channel) return setFormError('Channel is required.');
    if (product === 'firmware' && payload.variants.length === 0) {
      return setFormError('Pick at least one hardware variant.');
    }
    setSaving(true);
    try {
      if (editing === 'new') await createRelease(payload);
      else await updateRelease(editing, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    const label = `${productLabel} ${r.version} (${r.channel})`;
    if (!window.confirm(`Delete the release entry ${label}? This cannot be undone.`)) return;
    setDeleting(r.releaseId);
    setError(null);
    try {
      await deleteRelease(r.releaseId);
      await load();
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Release Management');
    } finally {
      setDeleting(null);
    }
  };

  const isFirmware = product === 'firmware';
  const suggestions = CHANNEL_SUGGESTIONS[product] || [];

  return (
    <div className="rm-page">
      <div className="rm-header">
        <div>
          <h2>Release Management</h2>
          <p className="rm-sub">
            What version of each REKKIE product is on which channel, and what changed in it.
          </p>
        </div>
        <Button variant="primary" onClick={openNew}>New release</Button>
      </div>

      <div className="rm-products" role="tablist" aria-label="Product">
        {products.map((p) => (
          <button
            key={p.key}
            role="tab"
            aria-selected={p.key === product}
            className={`rm-product-tab${p.key === product ? ' active' : ''}`}
            onClick={() => selectProduct(p.key)}
          >
            <span className="rm-product-dot" style={{ background: PRODUCT_BADGE_COLOR[p.key] || '#64748b' }} />
            {p.label}
          </button>
        ))}
      </div>

      <div className="rm-controls">
        <input
          type="text"
          placeholder="Filter by version, channel, change or author…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {channels.length > 1 && (
          <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} aria-label="Channel">
            <option value="">All channels</option>
            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {isFirmware && (
          <select value={variantFilter} onChange={(e) => setVariantFilter(e.target.value)} aria-label="Variant">
            <option value="">All variants</option>
            {firmwareVariants.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        )}
        {releases && (
          <span className="rm-count">
            {shown.length === releases.length ? `${releases.length} release${releases.length === 1 ? '' : 's'}` : `${shown.length} of ${releases.length}`}
          </span>
        )}
      </div>

      {error && <div className="rm-error">{error}</div>}

      {!releases && !error && <div className="rm-loading">Loading…</div>}

      {releases && releases.length === 0 && (
        <div className="rm-empty">
          <p>No releases recorded for {productLabel} yet.</p>
          <Button variant="outline" size="small" onClick={openNew}>Record the first one</Button>
        </div>
      )}

      {releases && releases.length > 0 && shown.length === 0 && (
        <div className="rm-empty"><p>No releases match the current filter.</p></div>
      )}

      {shown.length > 0 && (
        <ol className="rm-timeline">
          {shown.map((r) => {
            const current = currentByChannel[r.channel] === r.releaseId;
            return (
              <li key={r.releaseId} className={`rm-item${current ? ' current' : ''}`}>
                <div className="rm-marker" aria-hidden="true" />
                <div className="rm-card">
                  <div className="rm-card-head">
                    <span className="rm-version">{r.version}</span>
                    <span className="rm-channel">{r.channel}</span>
                    {current && <span className="rm-current" title={`Newest release on ${r.channel}`}>current</span>}
                    {(r.variants || []).map((v) => (
                      <span key={v} className="rm-variant" title={variantLabel(v)}>{v}</span>
                    ))}
                    <span className="rm-when" title={r.releasedAt || ''}>{formatInstant(r.releasedAt)}</span>
                  </div>

                  {r.title && <p className="rm-title">{r.title}</p>}

                  {r.changes.length > 0 ? (
                    <ul className="rm-changes">
                      {r.changes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  ) : (
                    <p className="rm-no-changes"><em>No changes listed</em></p>
                  )}

                  {r.notes && <p className="rm-notes">{r.notes}</p>}

                  <div className="rm-meta">
                    {r.link && <a href={r.link} target="_blank" rel="noopener noreferrer">{r.link}</a>}
                    <span>{r.createdByEmail || 'unknown author'}</span>
                    {r.updatedAt && r.updatedAt !== r.createdAt && r.updatedByEmail && (
                      <span>edited by {r.updatedByEmail}</span>
                    )}
                  </div>

                  <div className="rm-actions">
                    <Button variant="outline" size="small" onClick={() => openEdit(r)}>Edit</Button>
                    <Button
                      variant="danger"
                      size="small"
                      disabled={deleting === r.releaseId}
                      onClick={() => handleDelete(r)}
                    >
                      {deleting === r.releaseId ? 'Deleting…' : 'Delete'}
                    </Button>
                    <span className="rm-id">{r.releaseId}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <Modal
        isOpen={editing !== null}
        onClose={closeForm}
        title={editing === 'new' ? 'New release' : 'Edit release'}
        subtitle={productLabel}
        className="rm-modal"
      >
        <form className="rm-form" onSubmit={handleSave}>
          <div className="rm-form-row">
            <label>
              Version
              <input
                type="text"
                value={form.version}
                onChange={setField('version')}
                placeholder={VERSION_PLACEHOLDER[product] || ''}
                autoFocus
                required
              />
            </label>
            <label>
              Channel
              <input
                type="text"
                value={form.channel}
                onChange={setField('channel')}
                list={`rm-channels-${product}`}
                placeholder={suggestions[0] || ''}
                required
              />
              <datalist id={`rm-channels-${product}`}>
                {suggestions.map((s) => <option key={s} value={s} />)}
              </datalist>
            </label>
            <label>
              Released at
              <input type="datetime-local" step="1" value={form.releasedAt} onChange={setField('releasedAt')} />
            </label>
          </div>

          {isFirmware && (
            <fieldset className="rm-variants">
              <legend>Hardware variants this build targets</legend>
              {firmwareVariants.map((v) => (
                <label key={v.key} className="rm-variant-option">
                  <input
                    type="checkbox"
                    checked={form.variants.includes(v.key)}
                    onChange={() => toggleVariant(v.key)}
                  />
                  {v.label} <code>{v.key}</code>
                </label>
              ))}
            </fieldset>
          )}

          <label>
            Title <span className="rm-optional">optional</span>
            <input type="text" value={form.title} onChange={setField('title')} placeholder="One line summary" />
          </label>

          <label>
            Changes <span className="rm-optional">one per line</span>
            <textarea
              value={form.changesText}
              onChange={setField('changesText')}
              rows={8}
              placeholder={'Fixed goggles reconnect loop after sleep\nNew resort live map for Whistler'}
            />
          </label>

          <label>
            Notes <span className="rm-optional">optional, internal</span>
            <textarea value={form.notes} onChange={setField('notes')} rows={3} />
          </label>

          <label>
            Link <span className="rm-optional">optional</span>
            <input
              type="url"
              value={form.link}
              onChange={setField('link')}
              placeholder="https://play.google.com/… or a build / PR link"
            />
          </label>

          {formError && <div className="rm-error">{formError}</div>}

          <div className="rm-form-actions">
            <Button variant="outline" type="button" onClick={closeForm} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : (editing === 'new' ? 'Create release' : 'Save changes')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ReleaseManagementPage;
